/**
 * SquadCup (Andrea 2026-10-08): la coppa fra i vincitori delle leghe
 * dell'app. Pagina per tutti: trofeo, edizione (scelta se piu' d'una),
 * partecipanti e tabellone.
 */

import { useState } from "react";
import { useSquadCupEdizione, useSquadCupEdizioni } from "../../hooks/useSquadCup";
import { TeamBadge } from "../common/TeamBadge";
import { etichetta } from "../common/etichette";
import { MobileShell } from "../shell/MobileShell";
import { Tabellone } from "./Tabellone";
import { tr } from "../../i18n";

const TITOLO: Record<string, string> = {
  campione: "campione",
  coppa: tr("vincitore di coppa"),
  invito: "invitata",
};

export function SquadCupPage() {
  const { data: edizioni, isLoading } = useSquadCupEdizioni();
  const [scelta, setScelta] = useState<string | null>(null);
  const idCorrente = scelta ?? edizioni?.[0]?.id ?? null;
  const { data: ed } = useSquadCupEdizione(idCorrente);

  return (
    <MobileShell logoSuffix="SQUADCUP">
      <div className="card relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 20% 0%, rgba(212,175,55,0.22), transparent 55%)",
          }}
        />
        <div className="relative flex items-center gap-4">
          <img
            src="/squadcup-trofeo.webp"
            alt={tr("Trofeo SquadCup")}
            className="w-20 sm:w-28 shrink-0 drop-shadow-[0_8px_16px_rgba(0,0,0,0.5)]"
          />
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
              {tr("La coppa delle coppe")}
            </div>
            <h2 className="font-display text-2xl text-fg">SquadCup</h2>
            <p className="text-xs text-fg-muted mt-1">
              {tr(
                tr(
                  "I vincitori delle leghe di FantaSquad si affrontano su giornate di Serie A: ogni squadra gioca con la formazione della propria lega e vale il punteggio che fa lì. Eliminazione diretta, la più forte dell'app alza il trofeo.",
                ),
              )}
            </p>
          </div>
        </div>
      </div>

      {isLoading && <div className="card text-sm text-fg-muted text-center">{tr("Carico…")}</div>}

      {edizioni && edizioni.length === 0 && (
        <div className="card text-sm text-fg-muted">
          {tr(
            tr(
              "Nessuna edizione ancora: la SquadCup parte quando l'app avrà abbastanza leghe con un campione in carica (almeno 8 squadre). Tieni d'occhio questa pagina.",
            ),
          )}
        </div>
      )}

      {edizioni && edizioni.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {edizioni.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => setScelta(e.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                e.id === idCorrente
                  ? "bg-accent text-ink border-accent"
                  : "border-primary/40 text-fg-muted hover:text-fg"
              }`}
            >
              {e.nome}
            </button>
          ))}
        </div>
      )}

      {ed && (
        <>
          <div className="card space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="font-display text-lg text-fg">{ed.nome}</div>
                <div className="text-[11px] text-fg-muted">
                  Serie A {ed.stagione_serie_a} · {etichetta(ed.stato)}
                  {ed.di_prova && tr(" · edizione di prova")} ·{" "}
                  {ed.regola === "campioni"
                    ? tr("solo i campioni")
                    : tr("campioni e vincitori di coppa")}
                </div>
              </div>
              {ed.vincitore && (
                <div className="flex items-center gap-2 rounded-full bg-accent/15 border border-accent/40 px-3 py-1">
                  <span aria-hidden>🦅</span>
                  <TeamBadge
                    nome={ed.vincitore.nome}
                    logoDataUrl={ed.vincitore.logo_data_url}
                    size={22}
                    variant="home"
                  />
                  <span className="text-sm font-bold text-accent">{ed.vincitore.nome}</span>
                </div>
              )}
            </div>
            {ed.note && <p className="text-xs text-fg-muted">{ed.note}</p>}
            <div className="flex flex-wrap gap-2 pt-1">
              {ed.partecipanti.map((p) => (
                <div
                  key={p.squadra.id}
                  className="flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary-deep/40 pl-1 pr-2.5 py-0.5"
                  title={tr("{{v1}} · {{v2}}", {
                    v1: p.squadra.lega_nome,
                    v2: TITOLO[p.titolo] ?? p.titolo,
                  })}
                >
                  <TeamBadge
                    nome={p.squadra.nome}
                    logoDataUrl={p.squadra.logo_data_url}
                    size={20}
                    variant="home"
                  />
                  <span className="text-xs text-fg">{p.squadra.nome}</span>
                  <span className="text-[10px] text-fg-muted">· {p.squadra.lega_nome}</span>
                </div>
              ))}
            </div>
          </div>
          <Tabellone ed={ed} />
        </>
      )}
    </MobileShell>
  );
}
